import { useEffect, useState } from "react";
import {
  IconBarbell,
  IconChevronRight,
  IconHourglass,
} from "@tabler/icons-react";
import {
  Badge,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import useCustomNavigation from "hooks/customNavigation";
import TimeUtils from "utils/time";
import AnalyticsService from "data/firestore/analytics";

export interface ValidationError {
  message: string;
}

export interface TugSessionData {
  id: string | null;
  method: string;
  dateTimeString: string;
  dateTimeStringLocal?: string;
  notes?: string;
  row?: number;
  sessions: number;
  duration: number;
  durationFinal: number;
  validationErrors: ValidationError[];
  save: (validate?: boolean) => Promise<void>;
}

export interface SessionListItemProps {
  session: TugSessionData;
  clickable?: boolean;
  autoImport?: boolean;
  showDocumentCreationStatus?: boolean;
  /** Render the start time in the viewer's tz (for another user's sessions). */
  localTime?: boolean;
  onChange?: () => void;
}

const SessionListItem = ({
  autoImport = false,
  session,
  clickable = false,
  showDocumentCreationStatus = false,
  localTime = false,
  onChange,
}: SessionListItemProps) => {
  const navigate = useCustomNavigation();
  const [documentedCreated, setDocumentCreated] = useState(!!session.id);

  useEffect(() => {
    if (
      autoImport &&
      showDocumentCreationStatus &&
      !documentedCreated &&
      session.validationErrors.length <= 0
    ) {
      session.save(false).then(() => {
        setDocumentCreated(session.id != null);
        onChange?.();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let duration: React.ReactNode;
  if (session.durationFinal === -1) {
    duration = (
      <Tooltip title="active session">
        <IconButton>
          <IconHourglass />
        </IconButton>
      </Tooltip>
    );
  } else {
    duration = TimeUtils.elapsedToString(
      TimeUtils.breakdown(session.duration),
      2,
      true
    );
  }

  const handleClick = session.id
    ? () => {
        AnalyticsService.event("load_tug_session_from_list");
        navigate("tug", `?id=${session.id}`);
      }
    : undefined;

  return (
    <ListItemButton key={session.id} onClick={handleClick}>
      <ListItemAvatar style={{ marginRight: 15, textAlign: "right" }}>
        {duration}
      </ListItemAvatar>
      <ListItemText
        primary={session.method}
        secondary={`${session.row ? `Row ${session.row}: ` : ""}${
          localTime
            ? session.dateTimeStringLocal ?? session.dateTimeString
            : session.dateTimeString
        } ${session.notes ? ` \u00B7 ${session.notes}` : ""}`}
        style={{ marginRight: 10 }}
      />
      {session.sessions > 1 ? (
        <Tooltip title="Sessions" style={{ marginRight: 5 }}>
          <ListItemIcon>
            <Badge
              badgeContent={session.sessions}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              <IconBarbell color={blueGrey[500]} />
            </Badge>
          </ListItemIcon>
        </Tooltip>
      ) : null}
      {clickable ? (
        <ListItemIcon>
          <IconChevronRight />
        </ListItemIcon>
      ) : null}
    </ListItemButton>
  );
};

SessionListItem.displayName = "SessionListItem";

export default SessionListItem;
